/**
 * The shape of the agent's answer, readable: one row per field with its type,
 * allowed values and description. Accepts a JSON Schema (`properties`) or a
 * plain `{field: type}` map, which is what some platforms return. The raw JSON
 * is one click away.
 */

import { useState } from "react";
import { CodeBlock } from "@/components/ui/box";
import { Button } from "@/components/ui/button";
import { FieldChip } from "@/components/ui/pill";
import { formatJson } from "@/lib/format";

type Schema = Record<string, unknown>;

export interface SchemaField {
  path: string;
  type: string;
  required: boolean;
  enum: string[];
  description: string;
}

const isObject = (v: unknown): v is Schema => typeof v === "object" && v !== null && !Array.isArray(v);

function typeLabel(s: unknown): string {
  if (typeof s === "string") return s;
  if (!isObject(s)) return "any";
  if (typeof s.$ref === "string") return s.$ref.split("/").pop() ?? "object";
  const union = (s.anyOf ?? s.oneOf) as unknown;
  if (Array.isArray(union))
    return (
      union
        .map(typeLabel)
        .filter((t) => t !== "null")
        .join(" | ") || "any"
    );
  if (Array.isArray(s.type)) return s.type.filter((t) => t !== "null").join(" | ");
  if (s.type === "array") return `list of ${typeLabel(s.items)}`;
  if (typeof s.type === "string") return s.type;
  if (Array.isArray(s.enum)) return "enum";
  return isObject(s.properties) ? "object" : "any";
}

/** Every field, nested ones as dotted paths (`findings.site`), in schema order. */
export function schemaFields(schema: unknown, prefix = ""): SchemaField[] {
  if (!isObject(schema)) return [];
  const props = isObject(schema.properties) ? schema.properties : schema.type || schema.$ref ? null : schema;
  if (!props) return [];
  const required = Array.isArray(schema.required) ? (schema.required as string[]) : null;
  return Object.entries(props).flatMap(([name, sub]) => {
    const path = prefix ? `${prefix}.${name}` : name;
    const s = isObject(sub) ? sub : {};
    const field: SchemaField = {
      path,
      type: typeLabel(sub),
      required: required ? required.includes(name) : true,
      enum: Array.isArray(s.enum) ? s.enum.map(String) : [],
      description: typeof s.description === "string" ? s.description : "",
    };
    const inner = s.type === "array" ? s.items : s;
    const nested = isObject(inner) && isObject(inner.properties) ? schemaFields(inner, path) : [];
    return [field, ...nested];
  });
}

export function OutputSchema({ schema }: { schema: Schema }) {
  const [raw, setRaw] = useState(false);
  const fields = schemaFields(schema);
  return (
    <div className="flex flex-col">
      {raw || fields.length === 0 ? (
        <CodeBlock label="Output schema JSON">{formatJson(schema)}</CodeBlock>
      ) : (
        <table className="tbl text-xs" aria-label="Output fields">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => (
              <tr key={f.path}>
                <td className="font-mono font-medium">
                  {f.path}
                  {/* Readable text, not the form mark: this is a table to read, not a field to fill. */}
                  {!f.required && (
                    <span className="pl-1.5 font-sans text-xs font-normal text-faint">Optional</span>
                  )}
                </td>
                <td>
                  <span className="font-mono text-muted">{f.type}</span>
                  {f.enum.length > 0 && (
                    <span className="flex flex-wrap gap-1 pt-1">
                      {f.enum.map((v) => (
                        <FieldChip key={v}>{v}</FieldChip>
                      ))}
                    </span>
                  )}
                </td>
                <td className="text-muted">{f.description || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {fields.length > 0 && (
        <div className="border-t border-line-soft px-4 py-2">
          <Button size="sm" variant="ghost" onClick={() => setRaw(!raw)}>
            {raw ? "Show fields" : "Show JSON"}
          </Button>
        </div>
      )}
    </div>
  );
}
